import { useMemo, useState } from "react";
import { useDeviceCreditHistory } from "@/hooks/use-device-credit-history";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { History, RefreshCw, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface DeviceCreditHistoryProps {
  deviceId: string;
  /**
   * When false the API call is skipped until the section is expanded.
   * Defaults to true (auto-load when mounted).
   */
  autoLoad?: boolean;
}

export const DeviceCreditHistory = ({ deviceId, autoLoad = true }: DeviceCreditHistoryProps) => {
  const [enabled, setEnabled] = useState(autoLoad);
  const [selectedMonth, setSelectedMonth] = useState<string | "all">("all");

  const query = useDeviceCreditHistory(deviceId, enabled);
  const events = query.data?.events ?? [];
  const months = query.data?.months ?? [];

  const activeMonth = selectedMonth === "all" ? months[0] : selectedMonth;

  const monthEvents = useMemo(() => {
    if (!activeMonth) return [];
    return events.filter((e) => e.month === activeMonth);
  }, [events, activeMonth]);

  const chartData = useMemo(() => {
    if (monthEvents.length === 0) return [];
    const byDay: Record<string, { day: string; credits: number }> = {};
    for (const e of monthEvents) {
      const day = format(e.timestamp, "MMM dd");
      if (!byDay[day]) {
        byDay[day] = { day, credits: 0 };
      }
      byDay[day].credits += 1;
    }
    return Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day));
  }, [monthEvents]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-muted">
              <History className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">Credit History</CardTitle>
              <CardDescription>
                Per-image AI results consumed against this device's credits.
              </CardDescription>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (!enabled) setEnabled(true);
              else query.refetch();
            }}
            disabled={query.isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${query.isFetching ? "animate-spin" : ""}`} />
            {enabled ? "Refresh" : "Load"}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!enabled && (
          <p className="text-sm text-muted-foreground">
            Click Load to fetch credit history for this device.
          </p>
        )}

        {enabled && query.isLoading && (
          <p className="text-sm text-muted-foreground">Loading credit history…</p>
        )}

        {enabled && query.isError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {(query.error as Error)?.message || "Failed to load credit history."}
            </AlertDescription>
          </Alert>
        )}

        {enabled && !query.isLoading && events.length === 0 && !query.isError && (
          <p className="text-sm text-muted-foreground">
            No credit history recorded for this device yet.
          </p>
        )}

        {enabled && events.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="outline">
                {monthEvents.length} credits in {activeMonth}
              </Badge>
              <div className="ml-auto">
                <Select
                  value={selectedMonth}
                  onValueChange={(v) => setSelectedMonth(v as string)}
                >
                  <SelectTrigger className="w-[160px] h-8">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Latest month</SelectItem>
                    {months.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="h-[260px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value) => [value, "Credits"]}
                    labelFormatter={(label) => String(label)}
                  />
                  <Bar dataKey="credits" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} name="Credits" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DeviceCreditHistory;
